'use client';

import Image from 'next/image';
import { useEffect, useState } from 'react';

import { Button, Text } from '@/components/atomic/atoms';
import { staticMediaStoreBaseURL } from '@/config/common';
import usePwaDismissalStorage from '@/hooks/usePwaDismissalStorage';
import { isInStandaloneMode, isIos } from '@/lib/device';
import { useIsMobile } from '@/lib/screenResolution';

const IosInstallContent = ({ handleDismissIosPrompt }) => (
  <>
    <div className='px-4 py-2 rounded-lg bg-white text-brand text-sm font-normal transition shadow'>
      <div className='font-bold mb-2'>To Install The App</div>
      <div className='grid gap-2'>
        <div>
          Step 1: Tap on <span className='font-bold'>Share</span> icon
        </div>
        <div>
          Step 2: click
          <span className='font-bold'> Add to home screen</span>
        </div>
      </div>
    </div>
    <Button
      {...{
        className: 'px-4 py-2 rounded-lg text-sm transition text-white mt-2',
        children: 'Remind me Later',
        onClick: handleDismissIosPrompt
      }}
    />
  </>
);

const AndroidInstallContent = ({ handleDismiss, handleInstallClick }) => (
  <div className='flex justify-center gap-3'>
    <Button
      {...{
        className: 'px-4 py-2 rounded-lg text-sm transition text-white',
        children: 'Remind me Later',
        onClick: handleDismiss
      }}
    />
    <Button
      {...{
        className:
          'px-4 py-2 rounded-lg bg-white text-brand text-sm font-semibold transition shadow',
        children: 'Install Now',
        onClick: handleInstallClick
      }}
    />
  </div>
);

const InstallPromptBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  const [isMobile] = useIsMobile();
  const [isIosDismissed, dismissIosPrompt] = usePwaDismissalStorage();

  useEffect(() => {
    if (!isMobile) return () => {};
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  useEffect(() => {
    const shouldShowIosPrompt = () => {
      if (!isIos()) return false;
      if (isInStandaloneMode()) return false;

      return !isIosDismissed;
    };

    if (shouldShowIosPrompt()) {
      setShowInstallPrompt(true);
    }
  }, [isIosDismissed]);

  const handleDismiss = () => {
    setShowInstallPrompt(false);
  };

  const handleDismissIosPrompt = () => {
    setShowInstallPrompt(false);
    dismissIosPrompt();
  };

  return (
    showInstallPrompt && (
      <div
        className={`fixed bottom-4 left-4 right-4 max-w-md mx-auto bg-brand-gradient text-white rounded-xl shadow-lg px-1 py-2 flex items-start justify-between z-50`}
      >
        <div className='w-full max-w-sm px-1 py-2'>
          <div className='flex items-center mb-4 justify-center'>
            <Image
              alt='Hafla Partner App'
              className='rounded'
              height={0}
              src={`${staticMediaStoreBaseURL}/icons/hafla-partner-icon.svg`}
              style={{ height: 40, width: 40 }}
              width={0}
            />
            <div className='ml-4'>
              <Text
                {...{
                  className: 'text-lg font-bold',
                  content: 'Install Hafla Partner App'
                }}
              />
            </div>
          </div>
          <Text
            {...{
              className:
                'text-sm text-gray-300 mb-4 leading-relaxed text-center',
              content: 'Get real time booking info and order updates on the go'
            }}
          />
          {isIos() ? (
            <IosInstallContent {...{ handleDismissIosPrompt }} />
          ) : (
            <AndroidInstallContent
              {...{ handleDismiss, handleInstallClick }}
            />
          )}
        </div>
      </div>
    )
  );
};

export default InstallPromptBanner;
